export const analyzeSecurityThreats = async (alerts) => {
  // Always use backend proxy (never expose API key)
  try {
    const resp = await fetch("/api/analyze-threats", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        alerts: alerts.slice(0, 5),
        analysisType: "threat-assessment",
      }),
    });

    if (resp.ok) {
      const data = await resp.json();
      // If the proxy already returns the normalized fields
      if (typeof data?.riskScore !== "undefined") {
        return {
          riskScore: Number(data.riskScore ?? 70),
          criticalThreats: Array.isArray(data.criticalThreats)
            ? data.criticalThreats
            : [],
          recommendations: Array.isArray(data.recommendations)
            ? data.recommendations
            : [],
          _source: "backend-proxy",
          _modelUsed: data._modelUsed || "command",
          isRealAI: true,
          timestamp: new Date().toISOString(),
        };
      }

      // Otherwise, try to extract text v2 and parse JSON
      const parts = Array.isArray(data?.message?.content)
        ? data.message.content.map((c) => c && c.text).filter(Boolean)
        : [];
      const responseText = parts.join("\n") || data?.text || "";
      const parsed = safeParseJsonFromText(responseText);

      if (parsed) {
        const normalized = normalizeAIResponse(parsed, alerts);
        return {
          ...normalized,
          _source: "backend-proxy",
          _modelUsed: "command",
          isRealAI: true,
          timestamp: new Date().toISOString(),
        };
      }
    }
  } catch {
    // fallback
  }
  return getIntelligentAnalysis(alerts);
};

// Try to extract a strict JSON from a text
const safeParseJsonFromText = (txt) => {
  try {
    const m = txt && txt.match(/\{[\s\S]*\}/);
    return m ? JSON.parse(m[0]) : null;
  } catch {
    return null;
  }
};

// Normalize the AI output to the format expected by the dashboard
const normalizeAIResponse = (parsed, alerts) => {
  const highCount = alerts.filter((a) => a.severity === "High").length;
  const totalAlerts = alerts.length;

  let riskScore = Math.max(1, Math.min(10, Number(parsed.riskScore) || 7));
  if (riskScore < 7 && highCount > 10) {
    riskScore = Math.min(10, Math.max(7, highCount / 3 + 5));
  }

  const criticalThreats = Array.isArray(parsed.criticalThreats)
    ? parsed.criticalThreats.slice(0, 5)
    : alerts
        .filter((a) => a.severity === "High")
        .slice(0, 3)
        .map((a) => a.title);

  const recommendations = Array.isArray(parsed.recommendations)
    ? parsed.recommendations.slice(0, 5)
    : generateIntelligentRecommendations(highCount, totalAlerts);

  return {
    riskScore,
    criticalThreats,
    recommendations,
    analysisSummary: parsed.analysisSummary || `Analyzed ${totalAlerts} alerts`,
  };
};

// Heuristic fallback (no other dependencies)
const getIntelligentAnalysis = (alerts) => {
  const highAlerts = alerts.filter((a) => a.severity === "High");
  const mediumAlerts = alerts.filter((a) => a.severity === "Medium");
  const totalAlerts = alerts.length;
  const highCount = highAlerts.length;
  const mediumCount = mediumAlerts.length;

  let riskScore;
  if (highCount > 15) riskScore = 9;
  else if (highCount > 10) riskScore = 8;
  else if (highCount > 5) riskScore = 7;
  else if (highCount > 2) riskScore = 6;
  else if (mediumCount > 10) riskScore = 5;
  else riskScore = Math.min(10, Math.max(3, highCount * 2 + mediumCount * 0.5));

  const criticalThreats = highAlerts.slice(0, 5).map((a) => a.title);
  if (criticalThreats.length === 0 && mediumAlerts.length > 0) {
    criticalThreats.push(...mediumAlerts.slice(0, 2).map((a) => a.title));
  }

  return {
    riskScore,
    criticalThreats,
    recommendations: generateIntelligentRecommendations(highCount, totalAlerts),
    analysisSummary: `Analyzed ${totalAlerts} alerts: ${highCount} critical`,
    _source: "intelligent-fallback",
    _modelUsed: "data-driven-analysis",
    isRealAI: false,
    timestamp: new Date().toISOString(),
  };
};

// Recommandations generated based on alert severity
const generateIntelligentRecommendations = (highCount, totalAlerts) => {
  const recommendations = [];
  if (highCount > 20) {
    recommendations.push(
      "🚨 IMMEDIATE: Activate enterprise-wide incident response",
      "🔒 Isolate all critical infrastructure systems",
      "👥 Mobilize CISO and executive leadership team",
      "📊 Conduct deep forensic analysis",
      "🌐 Implement network-wide monitoring"
    );
  } else if (highCount > 10) {
    recommendations.push(
      "🔴 HIGH PRIORITY: Isolate systems with critical alerts",
      "🛡️ Activate incident response team",
      "📋 Review firewall rules",
      "🔍 Enhanced logging and monitoring",
      "🔄 Deploy emergency security patches"
    );
  } else if (highCount > 5) {
    recommendations.push(
      "🟡 ELEVATED RISK: Quarantine high-risk systems",
      "📖 Comprehensive security log review",
      "🔄 Update antivirus signatures",
      "👥 Notify security team",
      "📈 Review security metrics"
    );
  } else {
    recommendations.push(
      "🟢 STANDARD: Review security alerts",
      "⚡ Apply security patches",
      "👀 Monitor for attack indicators",
      "📚 Update security procedures",
      "🎯 Conduct security briefing"
    );
  }
  return recommendations.slice(0, 5);
};

// Test simple (optionnel)
export const testCohereConnection = async () => {
  const result = { directAPI: false, apiKeyPresent: false };
  const COHERE_API_KEY = process.env.REACT_APP_COHERE_API_KEY;
  result.apiKeyPresent = !!COHERE_API_KEY && COHERE_API_KEY.length > 20;

  if (!result.apiKeyPresent) return result;

  try {
    const resp = await fetch("https://api.cohere.ai/v2/chat", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${COHERE_API_KEY}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        model: "command-a-03-2025",
        messages: [{ role: "user", content: "Say OK" }],
        max_tokens: 5,
        temperature: 0,
      }),
    });
    result.directAPI = resp.ok;
  } catch {
    result.directAPI = false;
  }
  return result;
};

export default { analyzeSecurityThreats, testCohereConnection };
